import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import AuthGate from "@/components/AuthGate";
import OrgStatsTable, { type PortalCompany } from "@/components/peo/OrgStatsTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatSuppressed } from "@/lib/orgCode";

export const Route = createFileRoute("/partner/$slug")({
  head: () => ({
    meta: [
      { title: "Partner portal — Home Approach" },
      { name: "description", content: "Aggregate engagement for your client companies on Home Approach." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Partner portal — Home Approach" },
      { property: "og:description", content: "Aggregate engagement for your client companies." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: () => <AuthGate><PartnerPortal /></AuthGate>,
});

type Portal = { name: string; logo_url: string | null; companies: PortalCompany[]; total_enrolled: number | null };

function PartnerPortal() {
  const { slug } = Route.useParams();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["peo-portal", slug],
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_peo_portal", { _slug: slug });
      if (error) throw error;
      return data as unknown as Portal;
    },
  });
  const [name, setName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [msg, setMsg] = useState<string | null>(null);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);
    const { data: s } = await supabase.auth.getSession();
    const r = await fetch("/api/public/peo-add-client", {
      method: "POST",
      headers: { "content-type": "application/json", authorization: `Bearer ${s.session?.access_token ?? ""}` },
      body: JSON.stringify({ slug, name, logoUrl }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) return setMsg(j.error || "Couldn't add that company. Please try again.");
    setMsg(`Added. Code ${j.code} will work once Home Approach activates it (usually within one business day).`);
    setName(""); setLogoUrl("");
    qc.invalidateQueries({ queryKey: ["peo-portal", slug] });
  };

  if (q.isLoading) return <div className="p-10 text-muted-foreground">Loading…</div>;
  if (q.error || !q.data) return <div className="mx-auto max-w-xl p-10 text-foreground">You don't have access to this partner portal. Contact partnerships@homeapproach.com.</div>;
  const p = q.data;
  return (
    <main className="mx-auto max-w-6xl px-5 py-10">
      <div className="flex items-center gap-3">
        {p.logo_url && <img src={p.logo_url} alt="" className="h-10 w-auto" />}
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: "var(--heading-font)" }}>{p.name} portal</h1>
      </div>
      <p className="mt-2 text-muted-foreground">
        Employees join at <span className="font-mono">homeapproach.com/{slug}</span> using their company code. Total enrolled across clients: <strong>{formatSuppressed(p.total_enrolled)}</strong>
      </p>
      <section className="mt-8"><OrgStatsTable companies={p.companies} /></section>
      <section className="mt-10 max-w-xl rounded-xl border bg-card p-5">
        <h2 className="font-semibold text-foreground">Add a client company</h2>
        <form onSubmit={add} className="mt-3 space-y-3">
          <Input placeholder="Company name" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} maxLength={150} />
          <Input placeholder="Logo image URL (optional)" type="url" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} />
          <Button type="submit">Add company</Button>
          {msg && <p className="text-sm text-muted-foreground">{msg}</p>}
        </form>
      </section>
    </main>
  );
}
